/** Tipos e regras comuns aos codecs de disfarce. */

export const DisguiseKind = {
  /** Base-1024 sobre 1024 emojis estaveis. */
  Emoji: 'emoji',
  /** Base-4096 sobre um dicionario PT/EN, com pontuacao aleatoria. */
  UnicodeWords: 'unicode-words',
  /**
   * Base-16384 sobre ideogramas CJK. O mais denso — e o menos discreto.
   * Para quando o que importa e caber, nao passar despercebido.
   */
  Compact: 'compact',
  /** PNG com payload no LSB, ou WAV que soa como chiado. */
  Media: 'media',
} as const
export type DisguiseKind = (typeof DisguiseKind)[keyof typeof DisguiseKind]

export const MediaCarrier = {
  Png: 'png',
  Wav: 'wav',
} as const
export type MediaCarrier = (typeof MediaCarrier)[keyof typeof MediaCarrier]

/** Canal de envio no mensageiro. */
export type Transport = 'text' | 'document'

/**
 * Canal exigido por codec.
 *
 * `media` SO pode sair como documento. Enviado como foto ou mensagem de voz,
 * WhatsApp e Telegram recomprimem — e a recompressao destroi tanto o LSB do
 * PNG quanto o sinal do WAV. Nao e recomendacao: `assertTransport` recusa.
 */
export const REQUIRED_TRANSPORT: Readonly<Record<DisguiseKind, Transport>> = {
  emoji: 'text',
  'unicode-words': 'text',
  compact: 'text',
  media: 'document',
}

/**
 * Acima deste tamanho o payload vai para o R2 e a mensagem carrega apenas a
 * URL assinada, ela propria cifrada.
 */
export const R2_OFFLOAD_THRESHOLD_BYTES = 100 * 1024

export interface DisguisedText {
  readonly kind: 'emoji' | 'unicode-words' | 'compact'
  readonly transport: 'text'
  readonly text: string
}

export interface DisguisedFile {
  readonly kind: 'media'
  readonly transport: 'document'
  readonly bytes: Uint8Array
  readonly mimeType: string
  readonly fileName: string
  readonly carrier: MediaCarrier
}

export type Disguised = DisguisedText | DisguisedFile

export class DisguiseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DisguiseError'
  }
}

/** Barra o envio pelo canal errado antes que o payload seja destruido. */
export function assertTransport(kind: DisguiseKind, transport: Transport): void {
  const required = REQUIRED_TRANSPORT[kind]
  if (transport !== required) {
    throw new DisguiseError(
      `codec ${kind} exige envio como "${required}"; "${transport}" recomprime e destroi o payload`,
    )
  }
}
